<script lang="ts">
	/* eslint-disable no-alert */
	import { putObjectToUpload } from "@api/s3.js";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import type { HTMLAttributes } from "svelte/elements";
	import imageUpload from "@core/assets/icons/image-upload.svg?raw";
	import { Locale } from "@core/schema/Locale.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { UploadedFileBucket } from "@core/schema/UploadedFileBucket.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { maxFileSize } from "../../../config/maxFileSize.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import Button from "../common/Button.svelte";
	import Icon from "../common/Icon.svelte";
	import Media from "../common/Media.svelte";

	let {
		currentFiles = $bindable([]),
		/* eslint-disable prefer-const */
		destinationFolder,
		multiple = false,
		disabled = false,
		accept = "*/*",
		maximum = multiple ? Infinity : 1,
		bucket = UploadedFileBucket.Upload,
		required = false,
		onchange,
		...rest
		/* eslint-enable prefer-const */
	}: Omit<HTMLAttributes<HTMLDivElement>, "onchange"> & {
		destinationFolder: string;
		disabled?: boolean;
		currentFiles?: UploadedFile[];
		accept?: string | string[];
		maximum?: number;
		bucket?: Extract<UploadedFileBucket, UploadedFileBucket.Upload>;
		required?: boolean;
	} & (
			| {
					multiple: true;
					onchange?(this: void, files: UploadedFile[]): void;
			  }
			| {
					multiple?: false;
					onchange?(this: void, file: UploadedFile | undefined): void;
			  }
		) = $props();

	const globalSettings = getGlobalSettings();

	const { tenant } = getGlobalTemplateProps();
	const texts = {
		tooManyFiles: {
			[Locale.cs]: (max: number): string => `Maximální počet souborů je ${max}`,
			[Locale.en]: (max: number): string => `You can upload at most ${max} files`,
		}[tenant.locale],
		fileTooLarge: {
			[Locale.cs]: (name: string, sizeMb: number, maxMb: number): string =>
				`Soubor ${name} je příliš velký (${sizeMb} MB). Maximální povolená velikost souboru je ${maxMb} MB.`,
			[Locale.en]: (name: string, sizeMb: number, maxMb: number): string =>
				`The file ${name} is too large (${sizeMb} MB). The maximum allowed file size is ${maxMb} MB.`,
		}[tenant.locale],
		unsupportedType: {
			[Locale.cs]: (name: string): string => `Soubor '${name}' má nepodporovaný typ.`,
			[Locale.en]: (name: string): string => `The file '${name}' has an unsupported type.`,
		}[tenant.locale],
		waitForUpload: {
			[Locale.cs]: "Vyčkejte prosím na nahrání souboru",
			[Locale.en]: "Please wait for the upload to finish",
		}[tenant.locale],
		uploading: {
			[Locale.cs]: "Nahrávání",
			[Locale.en]: "Uploading",
		}[tenant.locale],
		dropFiles: {
			[Locale.cs]: "Sem přetáhněte soubory nebo",
			[Locale.en]: "Drop your files here or",
		}[tenant.locale],
		dropFile: {
			[Locale.cs]: "Sem přetáhněte soubor nebo",
			[Locale.en]: "Drop your file here or",
		}[tenant.locale],
		browse: {
			[Locale.cs]: "vyberte z úložiště",
			[Locale.en]: "browse your device",
		}[tenant.locale],
		previewAlt: {
			[Locale.cs]: "Náhled nahraného souboru",
			[Locale.en]: "Preview of the uploaded file",
		}[tenant.locale],
	};
	const uploadFunction = $derived(
		{
			[UploadedFileBucket.Upload]: async (key: string, file: File | Blob): Promise<UploadedFile> => {
				return await putObjectToUpload(globalSettings.PUBLIC_GRAPHQL_ENDPOINT, file, key);
			},
		}[bucket],
	);

	let alreadyUploaded: [File, UploadedFile][] = $state([]);

	const upload = createPromiseWithStatus(async (filesToUpload: File[]) => {
		return await Promise.all(
			Array.from(filesToUpload).map(async (file) =>
				uploadFunction(destinationFolder, file).then((s3File) => {
					alreadyUploaded.push([file, s3File]);
					return s3File;
				}),
			),
		);
	});

	let input = $state<HTMLInputElement | undefined>(undefined);

	function onChange(files: UploadedFile[]): void {
		if (multiple) {
			(onchange as ((this: void, files: UploadedFile[]) => void) | undefined)?.(files);
		} else {
			(onchange as ((this: void, file: UploadedFile | undefined) => void) | undefined)?.(files[0]);
		}
		alreadyUploaded = alreadyUploaded.filter(([_, s3File]) => currentFiles.some((file) => file.path === s3File.path));
	}

	onChange(currentFiles);

	async function uploadFiles(fileList: FileList): Promise<void> {
		try {
			const filesToUpload = Array.from(fileList).filter(
				(file) => !alreadyUploaded.some(([f]) => `${f.name}.${f.type}` === `${file.name}.${f.type}`),
			);

			if (maximum && filesToUpload.length + currentFiles.length > maximum) {
				alert(texts.tooManyFiles(maximum));
				return;
			}

			for (const file of filesToUpload) {
				if (file.size > maxFileSize) {
					alert(texts.fileTooLarge(file.name, Math.round(file.size / 1024 / 1024), maxFileSize / 1024 / 1024));
					return;
				}

				const allowedFileTypes = Array.isArray(accept) ? accept : accept.split(",");
				if (accept !== "*/*" && !allowedFileTypes.some((type) => file.type.startsWith(type))) {
					alert(texts.unsupportedType(file.name));
					return;
				}
			}

			const files = await upload.call(filesToUpload);
			if (multiple) {
				currentFiles = [...currentFiles, ...files];
			} else {
				// eslint-disable-next-line require-atomic-updates
				currentFiles = files;
			}
			onChange(currentFiles);
		} finally {
			if (input) {
				input.value = "";
			}
		}
	}

	function onDelete(file: UploadedFile): void {
		if (input) {
			input.value = "";
		}
		currentFiles = currentFiles.filter((search) => search.path !== file.path);
		onChange(currentFiles);
	}

	function onInputChange({ target }: Event): void {
		const input = target as HTMLInputElement;
		if (input.files) {
			void uploadFiles(input.files);
		}
	}

	const isDisabled = $derived(disabled || upload.value.status === "loading");

	let loadingInput = $state<HTMLInputElement | null>(null);
	$effect(() => {
		if (loadingInput) {
			loadingInput.setCustomValidity(texts.waitForUpload);
		}
	});
</script>

<div {...rest} class={["relative flex flex-col gap-4", rest.class]}>
	{#if upload.value.status === "loading"}
		<input bind:this={loadingInput} type="text" required class="absolute opacity-0" tabindex="-1" />
	{/if}
	{#if currentFiles.length < maximum}
		<div class="bg-white shadow-sm relative flex h-32 w-full items-center justify-center rounded-lg p-4">
			<input
				bind:this={input}
				type="file"
				class="absolute inset-0 opacity-0"
				class:cursor-pointer={!isDisabled}
				class:cursor-not-allowed={isDisabled}
				accept={Array.isArray(accept) ? accept.join(",") : accept}
				disabled={isDisabled}
				{multiple}
				onchange={onInputChange}
				{required}
			/>
			<div class="pointer-events-none flex h-full w-full items-center justify-center">
				{#if upload.value.status === "loading"}
					<span>{texts.uploading}</span>
				{:else}
					<span class="flex items-center gap-2">
						<Icon class="h-[2.875rem]" icon={imageUpload} />
						<span class="leading-[1.2]">
							{multiple ? texts.dropFiles : texts.dropFile}
							<span class="block underline">{texts.browse}</span>
						</span>
					</span>
				{/if}
			</div>
		</div>
	{/if}
	{#if currentFiles.length > 0}
		<div class="flex flex-col gap-2">
			{#each currentFiles as file (file.path)}
				<div class="bg-white rounded-lg pr-4 flex items-center gap-2 overflow-clip">
					{#if file.type.startsWith("image/")}
						<Media
							source={file}
							class="aspect-square w-24 object-contain"
							alt={file.title ?? texts.previewAlt}
							lightbox
							lightboxWidth={600}
							height={96}
							width={96}
						/>
					{/if}
					<div class="flex-1">
						<span class="inline-block max-w-48 break-all text-ellipsis overflow-clip">{file.title}</span>
					</div>
					<Button
						class="h-auto !w-12 grid place-items-center leading-none aspect-square !p-0"
						variant={ButtonVariant.Phlox}
						onclick={(): void => {
							onDelete(file);
						}}
					>
						×
					</Button>
				</div>
			{/each}
		</div>
	{/if}
</div>
